/**
 * Signing in, and the two screens that hang off it: asking for a password
 * reset, and completing one from the link in the email.
 *
 * There is no server field. This bundle was served by the relaii it talks to,
 * so asking which server to sign in to would be asking a question the page
 * already knows the answer to. Domain-first login — type `you@example.com`,
 * discover the server, then authenticate — is a native-app concern for v1.1,
 * where the client genuinely starts out not knowing where it is pointed.
 *
 * This module also holds the shell the other unauthenticated screens are built
 * from, so setup, login and invitation acceptance stay visually the same page.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement, ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import packageInfo from '../../package.json'

import { needsMfaEnrollment, useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import type { FieldErrors, MfaChallenge } from '@/api/types'
import { Button, SpaceLogo, TextField } from '@/components/ui'

import styles from './LoginPage.module.css'

/** Mirrors `Relaii.Accounts.Password`'s bounds. */
export const MIN_PASSWORD_LENGTH = 12
export const MAX_PASSWORD_LENGTH = 256

/**
 * Mirrors `Relaii.Accounts.User`'s username rules so a form can answer without
 * a round trip. The server remains the authority; this only spares somebody a
 * rejected submission for a rule nobody could have guessed.
 */
const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{1,38}[a-z0-9]$/
const RESERVED_USERNAMES = ['admin', 'system', 'relaii', 'everyone', 'here', 'channel', 'all']

export function validateUsername(value: string): string | null {
  if (value === '') return 'Choose a username.'
  if (value.length < 3) return 'Must be at least 3 characters.'
  if (value.length > 40) return 'Must be at most 40 characters.'
  if (value !== value.toLowerCase()) return 'Lowercase letters only.'
  if (!USERNAME_PATTERN.test(value)) {
    return 'Use lowercase letters, digits, dot, dash or underscore, starting and ending with a letter or digit.'
  }
  if (RESERVED_USERNAMES.includes(value)) return 'That username is reserved.'
  return null
}

/** The first message the server attached to a field, for binding to a control. */
export function firstFieldError(errors: FieldErrors, field: string): string | null {
  const messages = errors[field]
  return messages?.[0] ?? null
}

/**
 * The browser's zone, so timestamps read correctly from the first message
 * without anybody visiting their profile.
 */
export function localTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof zone === 'string' && zone !== '' ? zone : undefined
  } catch {
    // A browser with no resolvable zone is not a reason to fail a signup; the
    // server treats it as absent and it can be set later.
    return undefined
  }
}

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

// ── The shell the unauthenticated screens share ─────────────────────────────

export function Wordmark({ small = false }: { small?: boolean }): ReactElement {
  return (
    <span className={cx(styles.wordmark, small && styles.wordmarkSm)}>
      relaii
      <span className={styles.wordmarkDot} aria-hidden="true" />
    </span>
  )
}

export interface AuthShellProps {
  /** Widens the card for forms with more than a handful of fields. */
  wide?: boolean
  children: ReactNode
}

export function AuthShell({ wide = false, children }: AuthShellProps): ReactElement {
  return (
    <main className={styles.page}>
      <div className={cx(styles.frame, wide && styles.frameWide)}>{children}</div>
    </main>
  )
}

export interface AuthBrandProps {
  /** The Space's logo. Absent on a Space that has not uploaded one. */
  logoUrl?: string | null
  /** The Space's name. Absent during setup, when there is no Space yet. */
  name?: string | null
  /** Renders the Space's name as the page's `h1` rather than a line above it. */
  primary?: boolean
}

/**
 * Whose sign-in screen this is.
 *
 * When there is a Space to name, the Space leads: its logo, then its name, with
 * `relaii` underneath as the small line saying what the software is. Setup runs
 * before any of that exists, so there the wordmark still stands on its own.
 */
export function AuthBrand({ logoUrl, name, primary = false }: AuthBrandProps): ReactElement {
  const space = typeof name === 'string' && name.trim() !== '' ? name.trim() : null

  if (space === null) {
    return (
      <div className={styles.brand}>
        {primary ? (
          <h1 className={styles.brandHeading}>
            <Wordmark />
          </h1>
        ) : (
          <Wordmark small />
        )}
      </div>
    )
  }

  return (
    <div className={styles.brand}>
      <SpaceLogo src={logoUrl ?? null} name={space} size="xl" decorative />
      {primary ? (
        <h1 className={styles.brandSpace}>{space}</h1>
      ) : (
        <p className={styles.brandSpace}>{space}</p>
      )}
      <Wordmark small />
    </div>
  )
}

export function AuthCard({ children }: { children: ReactNode }): ReactElement {
  return <section className={styles.card}>{children}</section>
}

export function AuthCardBrand({ logoUrl, name }: { logoUrl?: string | null; name?: string | null }): ReactElement {
  return (
    <div className={styles.cardIdentity}>
      <div className={styles.cardBrand}>
        <SpaceLogo src={logoUrl ?? null} name={name ?? 'Relaii'} size="xl" decorative />
        <div>
          <span className={styles.cardBrandName}>relaii</span>
          {name === undefined || name === null || name.trim() === '' ? null : (
            <div className={styles.cardSpaceName}>{name.trim()}</div>
          )}
        </div>
      </div>
    </div>
  )
}

export interface AuthHeadingProps {
  title: string
  lede?: ReactNode
  /** The page's `h1` rather than a section heading within it. */
  primary?: boolean
}

export function AuthHeading({ title, lede, primary = false }: AuthHeadingProps): ReactElement {
  return (
    <div className={styles.heading}>
      {primary ? (
        <h1 className={styles.title}>{title}</h1>
      ) : (
        <h2 className={styles.title}>{title}</h2>
      )}
      {lede === undefined ? null : <p className={styles.lede}>{lede}</p>}
    </div>
  )
}

export interface AuthFormProps {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  children: ReactNode
}

/**
 * `noValidate` throughout: the browser's own bubbles cannot be styled, are not
 * announced consistently, and would pre-empt the messages these forms write
 * against each field.
 */
export function AuthForm({ onSubmit, children }: AuthFormProps): ReactElement {
  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {children}
    </form>
  )
}

export type AuthNoticeTone = 'info' | 'error' | 'success'

export interface AuthNoticeProps {
  tone: AuthNoticeTone
  children: ReactNode
}

/**
 * A failure interrupts — it is what the person's next action depends on — so it
 * is announced assertively; everything else waits its turn.
 */
export function AuthNotice({ tone, children }: AuthNoticeProps): ReactElement {
  const toneClass =
    tone === 'error' ? styles.noticeError : tone === 'success' ? styles.noticeSuccess : styles.noticeInfo

  return (
    <p className={cx(styles.notice, toneClass)} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </p>
  )
}

export interface AuthSummaryItem {
  term: string
  value: ReactNode
}

export function AuthSummary({ items }: { items: readonly AuthSummaryItem[] }): ReactElement {
  return (
    <dl className={styles.summary}>
      {items.map((item) => (
        <div key={item.term} className={styles.summaryRow}>
          <dt className={styles.summaryTerm}>{item.term}</dt>
          <dd className={styles.summaryValue}>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function AuthFooter({ children }: { children: ReactNode }): ReactElement {
  return <p className={styles.footer}>{children}</p>
}

// ── Error copy ──────────────────────────────────────────────────────────────

/** `null` when the server gave no retry hint, which reads as "shortly". */
export function describeRetry(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return 'in a moment'
  if (seconds < 60) return `in ${seconds} second${seconds === 1 ? '' : 's'}`

  const minutes = Math.ceil(seconds / 60)
  return `in about ${minutes} minute${minutes === 1 ? '' : 's'}`
}

/**
 * A rejected `fetch` is not an `ApiError` — it never reached a server — so the
 * network case is the fallback rather than a code in the switch.
 */
export function networkMessage(): string {
  return 'Could not reach the server. Check your connection and try again.'
}

function signInErrorMessage(error: unknown): string {
  if (!isApiError(error)) return networkMessage()

  switch (error.code) {
    case 'invalid_credentials':
    case 'unauthenticated':
      // Deliberately one message for a wrong password and an unknown account:
      // two would turn the form into a way of asking who has an account here.
      return 'That username or password is not correct.'
    case 'account_disabled':
      return 'This account has been disabled. An administrator on this server can turn it back on.'
    case 'rate_limited':
      return `Too many sign-in attempts. Try again ${describeRetry(error.retryAfterSeconds)}.`
    case 'setup_required':
      return 'This server has not been set up yet.'
    case 'invalid_mfa_code':
      return 'That code is not correct.'
    case 'invalid_mfa_token':
    case 'token_expired':
      return 'That sign-in step has expired. Sign in again from the start.'
    default:
      return error.message
  }
}

function resetErrorMessage(error: unknown): string {
  if (!isApiError(error)) return networkMessage()

  switch (error.code) {
    case 'token_expired':
      return 'That reset link has expired. Ask for a new one and use the most recent email.'
    case 'invalid_request':
      return 'That reset link is not valid, or it has already been used. Ask for a new one.'
    case 'rate_limited':
      return `Too many attempts. Try again ${describeRetry(error.retryAfterSeconds)}.`
    case 'validation_failed': {
      const messages = error.fieldErrors['password']
      const first = messages?.[0]
      return first === undefined ? error.message : `Password ${first}.`
    }
    default:
      return error.message
  }
}

// ── Route state ─────────────────────────────────────────────────────────────

interface RouteState {
  from: string
  reason: string | null
}

/**
 * `useRequireAuth` sends people here with where they were going and why they
 * were interrupted. Router state is untyped, so it is narrowed rather than
 * trusted.
 */
function readRouteState(state: unknown): RouteState {
  if (typeof state !== 'object' || state === null) return { from: '/', reason: null }

  const record: Record<string, unknown> = state as Record<string, unknown>
  const from = record['from']
  const reason = record['reason']

  return {
    // Only a same-site path is accepted: an absolute URL in router state would
    // make this form an open redirect.
    from: typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') ? from : '/',
    reason: typeof reason === 'string' && reason !== '' ? reason : null,
  }
}

// ── Sign in ─────────────────────────────────────────────────────────────────

interface SignInFormProps {
  onForgotPassword: () => void
}

function SignInForm({ onForgotPassword }: SignInFormProps): ReactElement {
  const { login, completeMfa } = useAuth()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const firstField = useRef<HTMLInputElement>(null)
  const codeField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (challenge === null) firstField.current?.focus()
    else codeField.current?.focus()
  }, [challenge])

  const onPasswordSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      setError(null)

      if (identifier.trim() === '' || password === '') {
        setError('Enter your username or email address, and your password.')
        return
      }

      setBusy(true)
      try {
        // Navigation is not done here: the effect in LoginPage watches the
        // session and moves once it actually exists, so there is one place that
        // decides where a signed-in visitor lands.
        const next = await login(identifier.trim(), password)
        if (next !== undefined) {
          setChallenge(next)
          setCode('')
        }
      } catch (caught) {
        setError(signInErrorMessage(caught))
        setPassword('')
      } finally {
        setBusy(false)
      }
    },
    [identifier, login, password],
  )

  const onCodeSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      if (challenge === null) return
      setError(null)

      if (code.trim() === '') {
        setError('Enter the code from your authenticator app, or a recovery code.')
        return
      }

      setBusy(true)
      try {
        await completeMfa(challenge.mfa_token, code.trim())
      } catch (caught) {
        setError(signInErrorMessage(caught))
        setCode('')
      } finally {
        setBusy(false)
      }
    },
    [challenge, code, completeMfa],
  )

  const backToPassword = useCallback((): void => {
    setChallenge(null)
    setCode('')
    setError(null)
    setPassword('')
  }, [])

  if (challenge !== null) {
    return (
      <AuthForm onSubmit={(event) => void onCodeSubmit(event)}>
        {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

        <AuthHeading
          title="Authenticator code"
          lede="Open the app you used when you set up two-factor authentication, or enter a recovery code."
        />

        <TextField
          ref={codeField}
          label="Code"
          name="one-time-code"
          autoComplete="one-time-code"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          hint="Six digits, or a recovery code such as a1b2-c3d4."
          required
        />

        <div className={styles.actions}>
          <Button type="submit" variant="primary" fullWidth loading={busy}>
            Continue
          </Button>
          <button type="button" className={styles.linkButton} onClick={backToPassword}>
            Use a different account
          </button>
        </div>
      </AuthForm>
    )
  }

  return (
    <AuthForm onSubmit={(event) => void onPasswordSubmit(event)}>
      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

      <TextField
        ref={firstField}
        label="Username or email"
        name="username"
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={identifier}
        onChange={(event) => setIdentifier(event.target.value)}
        required
      />

      <TextField
        label="Password"
        name="password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        required
      />

      <div className={styles.actions}>
        <Button type="submit" variant="primary" fullWidth loading={busy}>
          Sign in
        </Button>
        <div className={styles.secondaryActions}>
          <Link className={styles.linkButton} to="/register">
            Register with code
          </Link>
          <button type="button" className={styles.linkButton} onClick={onForgotPassword}>
            Forgot your password?
          </button>
        </div>
      </div>
    </AuthForm>
  )
}

// ── Request a reset ─────────────────────────────────────────────────────────

interface ForgotPasswordFormProps {
  onBack: () => void
}

function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps): ReactElement {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const firstField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    firstField.current?.focus()
  }, [])

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      setError(null)

      if (email.trim() === '') {
        setError('Enter the email address on your account.')
        return
      }

      setBusy(true)
      try {
        await api.post<{ ok: boolean }>('/auth/password-reset', { email: email.trim() })
        setSent(true)
      } catch (caught) {
        // The server answers 200 for an address it has never seen, so the only
        // failures that reach here are transport faults and the rate limit —
        // neither of which says anything about whether the account exists.
        if (isApiError(caught) && caught.code === 'rate_limited') {
          setError(`Too many requests. Try again ${describeRetry(caught.retryAfterSeconds)}.`)
        } else {
          setError(networkMessage())
        }
      } finally {
        setBusy(false)
      }
    },
    [email],
  )

  if (sent) {
    return (
      <div className={styles.form}>
        <AuthNotice tone="success">
          If there is an account for that address, a reset link is on its way. The link is good for
          a short while, and asking again replaces any earlier link.
        </AuthNotice>
        <Button variant="secondary" fullWidth onClick={onBack}>
          Back to sign in
        </Button>
      </div>
    )
  }

  return (
    <AuthForm onSubmit={(event) => void onSubmit(event)}>
      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

      <TextField
        ref={firstField}
        label="Email address"
        name="email"
        type="email"
        autoComplete="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        hint="We will send a link to set a new password."
        required
      />

      <div className={styles.actions}>
        <Button type="submit" variant="primary" fullWidth loading={busy}>
          Send reset link
        </Button>
        <button type="button" className={styles.linkButton} onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </AuthForm>
  )
}

// ── Complete a reset ────────────────────────────────────────────────────────

interface ResetPasswordFormProps {
  token: string
  onDone: (message: string) => void
}

function ResetPasswordForm({ token, onDone }: ResetPasswordFormProps): ReactElement {
  const { logout } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const firstField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    firstField.current?.focus()
  }, [])

  const longEnough = password.length >= MIN_PASSWORD_LENGTH
  const matches = confirmation !== '' && password === confirmation

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      setError(null)

      if (!longEnough) {
        setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`)
        return
      }

      if (password !== confirmation) {
        setError('The two passwords do not match.')
        return
      }

      setBusy(true)
      try {
        await api.post<{ ok: boolean }>('/auth/password-reset/confirm', { token, password })

        // A reset revokes every session, this browser's included. Clearing the
        // client's own idea of the session stops it acting on a cache the server
        // has already thrown away.
        await logout()
        onDone('Your password has been changed. Sign in with your new password.')
      } catch (caught) {
        setError(resetErrorMessage(caught))
      } finally {
        setBusy(false)
      }
    },
    [confirmation, logout, longEnough, onDone, password, token],
  )

  return (
    <AuthForm onSubmit={(event) => void onSubmit(event)}>
      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}

      <TextField
        ref={firstField}
        label="New password"
        name="new-password"
        type="password"
        autoComplete="new-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        error={password !== '' && !longEnough ? `Too short — ${MIN_PASSWORD_LENGTH} characters minimum.` : null}
        required
      />

      <TextField
        label="Confirm new password"
        name="confirm-password"
        type="password"
        autoComplete="new-password"
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
        error={confirmation !== '' && !matches ? 'The two passwords do not match.' : null}
        required
      />

      <Button type="submit" variant="primary" fullWidth loading={busy}>
        Set new password
      </Button>
    </AuthForm>
  )
}

// ── The page ────────────────────────────────────────────────────────────────

type Mode = 'signIn' | 'forgot' | 'reset'

export function LoginPage(): ReactElement {
  const { user, loading, server } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const { from, reason } = readRouteState(location.state)
  const resetToken = searchParams.get('reset') ?? ''
  const [asked, setAsked] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const mode: Mode = resetToken !== '' ? 'reset' : asked ? 'forgot' : 'signIn'
  const afterSignIn = needsMfaEnrollment(user, server) ? '/account' : from

  // A session that already exists — or one that has just been opened by the
  // form below — belongs somewhere other than the login screen.
  useEffect(() => {
    if (loading || user === null || mode !== 'signIn') return
    navigate(afterSignIn, { replace: true })
  }, [afterSignIn, loading, mode, navigate, user])

  const onResetDone = useCallback(
    (message: string): void => {
      setNotice(message)
      setAsked(false)
      // Dropping the token from the address stops a reload replaying a link
      // that has already been spent.
      setSearchParams({}, { replace: true })
    },
    [setSearchParams],
  )

  const serverName = server?.display_name ?? server?.server_id ?? null

  return (
    <AuthShell>
      <AuthCard>
        <AuthCardBrand logoUrl={server?.logo_url ?? null} name={serverName} />
        {mode === 'signIn' ? (
          <>
            <AuthHeading primary title="Sign in to your Space" lede="Your conversations, all in one place." />
            {reason === null ? null : <AuthNotice tone="info">{reason}</AuthNotice>}
            {notice === null ? null : <AuthNotice tone="success">{notice}</AuthNotice>}
            <SignInForm
              onForgotPassword={() => {
                setNotice(null)
                setAsked(true)
              }}
            />
          </>
        ) : null}

        {mode === 'forgot' ? (
          <>
            <AuthHeading
              title="Forgot your password"
              lede="Tell us the address on your account and we will send a link to set a new password."
            />
            <ForgotPasswordForm onBack={() => setAsked(false)} />
          </>
        ) : null}

        {mode === 'reset' ? (
          <>
            <AuthHeading
              title="Choose a new password"
              lede="Setting a new password signs you out everywhere else on this server."
            />
            <ResetPasswordForm token={resetToken} onDone={onResetDone} />
          </>
        ) : null}
      </AuthCard>

      <AuthFooter>
        <a className={styles.footerLink} href="https://relaii.app">
          Running version {server?.product_version ?? packageInfo.version} of Relaii
        </a>
      </AuthFooter>
    </AuthShell>
  )
}
